import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryBidActivityCount,
    getItemSummaryBidCount,
    getItemSummaryBuyNowPrice,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogStatus,
    getItemSummaryCurrency,
    getItemSummaryIsAvailable,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
    getItemSummaryLeadingBid,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';

type Props = {
    className?: string;
    fullWidth?: boolean;
    isCatalogCard?: boolean;
    itemId: number;
    onNavigate?: () => void;
    seeSoldPriceHover?: (isHovered: boolean) => void;
};

const PriceRowComponent = ({
    className,
    fullWidth = true,
    isCatalogCard,
    itemId,
    onNavigate,
    seeSoldPriceHover = noop,
}: Props) => {
    const formatMessage = useAppFormatMessage();
    const navigate = useNavigate();
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const bidActivityCount = useAppSelector((state) => getItemSummaryBidActivityCount(state, itemId));
    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const buyNowPrice = useAppSelector((state) => getItemSummaryBuyNowPrice(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const itemUrl = useAppSelector((state) => getItemSummaryUrl(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const numberOfBids = Math.max(bidCount, bidActivityCount);

    const isDone = catalogStatus === AuctionStatus.Done;
    const buyNowAvailable = buyNowStatus === 1 && isDone;
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const salePriceFormatted = salePrice > 0 ? salePrice : 0;
    const currentPrice = leadingBid || salePriceFormatted || startPrice || 0;
    const currentPriceFormatted = currentPrice > 0 ? currentPrice : 0;

    const gatewayClassName = 'h-full tracking-[0.5px] smMin:text-sm';

    let primaryReturnComponent: ReactNode = null;

    if (!isDone && isPaused) {
        primaryReturnComponent = (
            <ItemPriceGatewayContainer
                className={gatewayClassName}
                hideIcon={isCatalogCard}
                itemId={itemId}
                showChildren
            >
                <H5>{formatMessage(['lotWithdrawn'])}</H5>
            </ItemPriceGatewayContainer>
        );
    } else if (isLotOpen) {
        primaryReturnComponent = (
            <div className="flex flex-col [&>*]:flex-[1_1_50%]">
                <div className="flex flex-row">
                    <H5>
                        <span className="mr-xs">
                            <FormattedCurrency
                                convert
                                noFraction
                                sourceCurrency={currency}
                                value={currentPriceFormatted}
                            />
                        </span>
                    </H5>
                    <span className="-mt-3xs -ml-2xs">
                        <Caption>
                            {formatMessage(['bidCount'], {
                                values: {
                                    formattedBid: (
                                        <FormattedNumber
                                            key="formatted_bid"
                                            value={numberOfBids}
                                        />
                                    ),
                                    numberOfBids: numberOfBids,
                                },
                            })}
                        </Caption>
                    </span>
                </div>
            </div>
        );
    } else if (buyNowAvailable) {
        primaryReturnComponent = (
            <span className="leading-[22px]">
                <span className="flex flex-wrap items-center gap-x-2xs text-text-primary">
                    <H5>
                        {formatMessage(['buy_now_for'], {
                            values: {
                                price: (
                                    <FormattedCurrency
                                        key="buy-now-for-price"
                                        sourceCurrency={currency}
                                        value={buyNowPrice}
                                    />
                                ),
                            },
                        })}
                    </H5>
                </span>
            </span>
        );
    } else if (isAvailable && !isUnsold && !isCatalogOnly) {
        primaryReturnComponent = (
            <span className="flex flex-wrap items-center gap-x-2xs text-text-primary">
                <H5>
                    <FormattedCurrency
                        convert
                        noFraction
                        sourceCurrency={currency}
                        value={currentPriceFormatted}
                    />
                </H5>
                <Caption className="text-text-secondary">
                    {!bidActivityCount
                        ? null
                        : formatMessage(['bidCount'], {
                              values: {
                                  formattedBid: (
                                      <FormattedNumber
                                          key="formatted_bid"
                                          value={bidActivityCount}
                                      />
                                  ),
                                  numberOfBids: bidActivityCount,
                              },
                          })}
                </Caption>
            </span>
        );
    } else {
        let returnComponentChild: ReactNode = null;

        if (!salePrice && isSold) {
            returnComponentChild = (
                <ItemPriceGatewayContainer
                    className={gatewayClassName}
                    hideIcon={isCatalogCard}
                    itemId={itemId}
                    runFunction={() => {
                        onNavigate?.();
                        navigate({ pathname: itemUrl });
                    }}
                >
                    <H5>{formatMessage(['sold'])}</H5>
                </ItemPriceGatewayContainer>
            );
        } else if (Boolean(salePrice) && isSold) {
            returnComponentChild = (
                <div
                    onMouseOut={() => seeSoldPriceHover(false)}
                    onMouseOver={() => seeSoldPriceHover(true)}
                >
                    <ItemPriceGatewayContainer
                        className={gatewayClassName}
                        hideIcon={isCatalogCard}
                        itemId={itemId}
                        runFunction={() => {
                            onNavigate?.();
                            navigate({ pathname: itemUrl });
                        }}
                    >
                        <H5>
                            {formatMessage(['sold_for'], {
                                values: {
                                    amount: (
                                        <FormattedCurrency
                                            key="sold-for-amount"
                                            noFraction
                                            sourceCurrency={currency}
                                            value={salePrice}
                                        />
                                    ),
                                },
                            })}
                        </H5>
                    </ItemPriceGatewayContainer>
                </div>
            );
        } else if (isDone && isPaused) {
            returnComponentChild = (
                <ItemPriceGatewayContainer
                    className={gatewayClassName}
                    hideIcon={isCatalogCard}
                    itemId={itemId}
                >
                    {isCatalogOnly ? <H5>{formatMessage(['lot_closed'])}</H5> : null}
                </ItemPriceGatewayContainer>
            );
        } else if (isUnsold) {
            returnComponentChild = (
                <ItemPriceGatewayContainer
                    className={gatewayClassName}
                    hideIcon={isCatalogCard}
                    itemId={itemId}
                >
                    <H5>{formatMessage(['lot_unsold_pending'])}</H5>
                </ItemPriceGatewayContainer>
            );
        } else if (isPassed) {
            returnComponentChild = (
                <ItemPriceGatewayContainer
                    className={gatewayClassName}
                    hideIcon={isCatalogCard}
                    itemId={itemId}
                    showChildren
                >
                    <H5>
                        {isCatalogOnly ? formatMessage(['lot_closed']) : formatMessage(['itemBidding', 'passedText'])}
                    </H5>
                </ItemPriceGatewayContainer>
            );
        }

        primaryReturnComponent = (
            <span className="flex flex-wrap items-center gap-x-2xs text-text-primary">{returnComponentChild}</span>
        );
    }

    return (
        <div className={cn('mt-2xs flex mobile:mt-0', fullWidth && 'w-full', className)}>{primaryReturnComponent}</div>
    );
};

export const PriceRow = memo<Props>(PriceRowComponent);
